import React, { useState, useEffect } from "react"
import axios from "axios"
import parse from "html-react-parser"
import { trackCustomEvent } from "gatsby-plugin-google-analytics"

import { useSiteTranslations } from "@hooks/useSiteTranslations"
import { TextInput } from "@components/common/form/TextInput"
import { DropDown } from "@components/common/form/DropDown"
import { TextArea } from "@components/common/form/TextArea"
import { CheckBox } from "@components/common/form/CheckBox"
import { Cta } from "@components/common/cta/Cta"

import * as s from "./Contact.module.scss"

export const Contact = ({ data }) => {
  const [isClient, setClient] = useState(false)
  const key = isClient ? "client" : "server"
  const {
    generalTranslations: { general },
  } = useSiteTranslations()

  useEffect(() => {
    setClient(true)
  }, [])

  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const competitionForm = data.type === "competition"
  const competitionPlaceholder =
    data.messagePlaceholder && data.messagePlaceholder

  const [formValue, setFormValue] = useState({
    FName: {
      value: "",
      required: true,
      errored: false,
    },
    LName: {
      value: "",
      required: true,
      errored: false,
    },
    emailAddress: {
      value: "",
      required: true,
      pattern:
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      errored: false,
    },
    phoneNumber: {
      value: "",
      errored: false,
    },
    destination: {
      value: "",
      required: true,
      errored: false,
    },
    message: {
      value: "",
      required: true,
      errored: false,
    },
    premission: {
      value: false,
      errored: false,
      required: true,
    },
  })

  const usedContacts = []

  const _submit = async () => {
    if (sending) return
    setSending(true)
    let hasError = false

    const _formVal = { ...formValue }

    Object.keys(_formVal).forEach(function (item) {
      const field = _formVal[item]

      if (field.required && !field.value) {
        hasError = true
        _formVal[item].errored = true
      } else {
        _formVal[item].errored = false
      }

      if (field.required && field.pattern) {
        if (!field.pattern.test(String(field.value).toLowerCase())) {
          hasError = true

          _formVal[item].errored = true
        } else {
          _formVal[item].errored = false
        }
      }
    })
    setFormValue(_formVal)

    if (hasError) {
      setSending(false)
    } else {
      try {
        /*eslint-disable*/
        let values = {}
        Object.keys(formValue).forEach(function (item) {
          values[item] = formValue[item].value
        })

        const contactRes = await axios.post(
          `/.netlify/functions/contact-form`,
          values,
        )
        /*eslint-enable*/
        trackCustomEvent({
          category: "Contact Form",
          action: "Submit",
        })
      } catch (e) {
        console.log("form error")
      }

      setSent(true)
      setSending(false)
    }
  }

  useEffect(() => {
    if (competitionForm) {
      const contact = data.contacts.map(contact => {
        return {
          email: contact.contact.acfContact.emailAddress,
        }
      })

      setFormValue({
        ...formValue,
        destination: {
          ...formValue["destination"],
          value: contact[0].email,
        },
      })
    }
  }, [])

  if (data.contacts.length < 1) {
    return <></>
  }

  if (!isClient) return null

  return (
    <div className={s.Wrapper} key={key}>
      <div className={s.Inner}>
        <div className={s.TextPanel}>
          <h3 className={s.TextPanel__title}>{data.title}</h3>
          <p className={s.TextPanel__blurb}>{data.blurb}</p>
        </div>

        <div className={`${s.FormPanel}`}>
          <div
            className={`${s.FromPanelconfirmation} ${
              sent && s.FromPanelconfirmation___sent
            }`}
          >
            <span>{data.confirmationMessage || "Message sent"}</span>
          </div>

          {!sent && (
            <div
              className={`${s.FormPanelInner} ${
                sending && s.FormPanelInner___sending
              } ${sent && s.FormPanelInner___sent}`}
            >
              <div className={s.FormPanelInner__row}>
                <TextInput
                  placeholder={general.contactForm.firstNamePlaceholderText}
                  value={formValue.FName.value}
                  error={formValue.FName.errored}
                  onChange={e => {
                    setFormValue({
                      ...formValue,
                      FName: { ...formValue["FName"], value: e.target.value },
                    })
                  }}
                />

                <TextInput
                  placeholder={general.contactForm.lastNamePlaceholderText}
                  value={formValue.LName.value}
                  error={formValue.LName.errored}
                  onChange={e => {
                    setFormValue({
                      ...formValue,
                      LName: { ...formValue["LName"], value: e.target.value },
                    })
                  }}
                />
              </div>
              <div className={s.FormPanelInner__row}>
                <TextInput
                  placeholder={general.contactForm.emailAddressPlaceholderText}
                  value={formValue.emailAddress.value}
                  error={formValue.emailAddress.errored}
                  onChange={e => {
                    setFormValue({
                      ...formValue,
                      emailAddress: {
                        ...formValue["emailAddress"],
                        value: e.target.value,
                      },
                    })
                  }}
                />
              </div>

              <div className={s.FormPanelInner__row}>
                <TextInput
                  placeholder={general.contactForm.phoneNumberPlaceholderText}
                  value={formValue.phoneNumber.value}
                  error={formValue.phoneNumber.errored}
                  onChange={e => {
                    setFormValue({
                      ...formValue,
                      phoneNumber: {
                        ...formValue["phoneNumber"],
                        value: e.target.value,
                      },
                    })
                  }}
                />

                {!competitionForm && (
                  <DropDown
                    placeholder={
                      general.contactForm.destinationSelectDropdownText
                    }
                    value={formValue.destination.value}
                    error={formValue.destination.errored}
                    options={data.contacts.map((contact, i) => {
                      const { acfContact } = contact.contact
                      let emailValue = acfContact.emailAddress

                      if (usedContacts.includes(emailValue)) {
                        emailValue = emailValue.replace("@", `+${i}@`)
                      }

                      usedContacts.push(emailValue)

                      return {
                        label: contact.contact.title,
                        value: emailValue,
                      }
                    })}
                    onChange={value => {
                      setFormValue({
                        ...formValue,
                        destination: {
                          ...formValue["destination"],
                          value: value,
                        },
                      })
                    }}
                  />
                )}
              </div>

              <div className={s.FormPanelInner__row}>
                <TextArea
                  placeholder={
                    competitionPlaceholder
                      ? competitionPlaceholder
                      : general.contactForm.competitionSelectDropdownText
                  }
                  value={formValue.message.value}
                  error={formValue.message.errored}
                  onChange={e => {
                    setFormValue({
                      ...formValue,
                      message: {
                        ...formValue["message"],
                        value: e.target.value,
                      },
                    })
                  }}
                />
              </div>

              <div className={s.FormPanelInner__row}>
                <CheckBox
                  label={parse(general.contactForm.privacyPolicyText)}
                  value={formValue.premission.value}
                  error={formValue.premission.errored}
                  onChange={e => {
                    setFormValue({
                      ...formValue,
                      premission: { ...formValue["premission"], value: e },
                    })
                  }}
                />
              </div>

              <Cta title={"Send Email"} onClick={_submit} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
